import { useMemo } from "react";
import {
  useIsRestoring,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import {
  defaultGuestKit,
  fetchKits,
  gearKeys,
  generateId,
  GUEST,
  type Item,
  type Kit,
} from "~/state/sync";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type { KitPatch } from "~/api/generated/types.gen";

export type KitInput = {
  name: string;
  notes?: string | null;
  color?: string | null;
};

function byName(a: Kit, b: Kit) {
  return a.name.localeCompare(b.name);
}

function useKitsQuery() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const isRestoring = useIsRestoring();
  return useQuery({
    queryKey: gearKeys.kits(userId ?? GUEST),
    // A guest starts with the same starter kit the database gives an account.
    queryFn: userId ? fetchKits : () => [defaultGuestKit()],
    ...(userId ? {} : { staleTime: Infinity, gcTime: Infinity }),
    enabled: userId ? true : !isRestoring,
  });
}

export function useKits() {
  const { data } = useKitsQuery();
  return useMemo(() => (data ?? []).slice().sort(byName), [data]);
}

export function useKit(kitId: string | undefined) {
  const { data } = useKitsQuery();
  return useMemo(
    () =>
      kitId ? ((data ?? []).find((kit) => kit.id === kitId) ?? null) : null,
    [data, kitId],
  );
}

export function useKitActions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  function invalidate() {
    if (!userId) return;
    void queryClient.invalidateQueries({ queryKey: gearKeys.kits(userId) });
    void queryClient.invalidateQueries({ queryKey: gearKeys.items(userId) });
  }

  const createMutation = useMutation({
    mutationFn: async (input: KitInput) => {
      const id = generateId();
      if (!userId) {
        const now = new Date().toISOString();
        queryClient.setQueryData<Kit[]>(gearKeys.kits(GUEST), (rows) => [
          ...(rows ?? []),
          {
            id,
            owner_id: GUEST,
            name: input.name.trim(),
            notes: input.notes?.trim() || null,
            color: input.color ?? "#3D5A80",
            deleted: false,
            created_at: now,
            updated_at: now,
          },
        ]);
        return id;
      }
      await unwrap(
        api.putKit({
          path: { id },
          body: {
            name: input.name.trim(),
            notes: input.notes?.trim() || null,
            color: input.color ?? "#3D5A80",
          },
        }),
      );
      return id;
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      kitId,
      input,
    }: {
      kitId: string;
      input: Partial<KitInput>;
    }) => {
      const patch: KitPatch = {};
      if (input.name !== undefined) patch.name = input.name.trim();
      if (input.notes !== undefined) patch.notes = input.notes?.trim() || null;
      if (input.color !== undefined) patch.color = input.color;
      if (Object.keys(patch).length === 0) return;
      if (!userId) {
        queryClient.setQueryData<Kit[]>(gearKeys.kits(GUEST), (rows) =>
          (rows ?? []).map((row) =>
            row.id === kitId
              ? { ...row, ...patch, updated_at: new Date().toISOString() }
              : row,
          ),
        );
        return;
      }
      await unwrap(api.patchKit({ path: { id: kitId }, body: patch }));
    },
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: async (kitId: string) => {
      if (!userId) {
        queryClient.setQueryData<Item[]>(gearKeys.items(GUEST), (rows) =>
          (rows ?? []).map((row) =>
            row.kit_id === kitId ? { ...row, kit_id: null } : row,
          ),
        );
        queryClient.setQueryData<Kit[]>(gearKeys.kits(GUEST), (rows) =>
          (rows ?? []).filter((row) => row.id !== kitId),
        );
        return;
      }
      // The API detaches the kit's items rather than cascading them away.
      await unwrap(api.deleteKit({ path: { id: kitId } }));
    },
    onSuccess: invalidate,
  });

  return {
    /** Resolves to the new kit id. */
    create: (input: KitInput) => createMutation.mutateAsync(input),
    update: (kitId: string, input: Partial<KitInput>): void =>
      updateMutation.mutate({ kitId, input }),
    /** Awaitable variant of `update`, for callers that must know it landed. */
    updateAsync: (kitId: string, input: Partial<KitInput>) =>
      updateMutation.mutateAsync({ kitId, input }),
    remove: (kitId: string): void => removeMutation.mutate(kitId),
  };
}
